'use client';

import { useState } from 'react';
import ToolHero from '@/components/tools/ToolHero';
import ToolCTA from '@/components/tools/ToolCTA';
import CopyButton from '@/components/tools/CopyButton';

type Reason =
  | 'New Opportunity'
  | 'Career Change'
  | 'Relocation'
  | 'Personal Reasons'
  | 'Going Back to School'
  | 'Other';

type Relationship = 'Great' | 'Good' | 'Neutral' | 'Prefer Not to Say';

type Tone = 'Standard' | 'Warm' | 'Formal';

function getDefaultLastDay(): string {
  const date = new Date();
  date.setDate(date.getDate() + 14);
  return date.toISOString().split('T')[0];
}

function formatDate(dateStr: string): string {
  const date = new Date(dateStr + 'T00:00:00');
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

function getTodayFormatted(): string {
  return new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

function generateLetter(
  name: string,
  manager: string,
  company: string,
  position: string,
  lastDay: string,
  reason: Reason,
  relationship: Relationship,
  offerTransition: boolean,
  tone: Tone
): string {
  const formattedLastDay = formatDate(lastDay);
  const today = getTodayFormatted();

  // --- Greeting ---
  const greetings: Record<Tone, string> = {
    Standard: `Dear ${manager},`,
    Warm: `Dear ${manager},`,
    Formal: `Dear Mr./Ms. ${manager},`,
  };

  // --- Opening paragraph ---
  const openings: Record<Tone, string> = {
    Standard: `I am writing to formally notify you that I am resigning from my position as ${position} at ${company}. My last day of work will be ${formattedLastDay}.`,
    Warm: `I hope this letter finds you well. After much thought and consideration, I have made the difficult decision to resign from my role as ${position} at ${company}. My last working day will be ${formattedLastDay}.`,
    Formal: `Please accept this letter as formal notification of my resignation from the position of ${position} at ${company}, effective ${formattedLastDay}.`,
  };

  // --- Reason paragraph ---
  const reasonParagraphs: Record<Reason, Record<Tone, string>> = {
    'New Opportunity': {
      Standard: `I have accepted a new position that will further my professional growth and career goals. This was not an easy decision, as I have truly valued my time at ${company}.`,
      Warm: `I have been presented with an exciting new opportunity that aligns closely with my long-term career aspirations. Please know that this decision was not made lightly -- my experience at ${company} has been incredibly meaningful to me.`,
      Formal: `I have accepted a position elsewhere that represents a significant advancement in my career trajectory. I wish to express that this decision was reached after careful deliberation regarding my professional development.`,
    },
    'Career Change': {
      Standard: `I have decided to pursue a career change that aligns more closely with my long-term goals. This decision comes after considerable reflection on the direction I want to take professionally.`,
      Warm: `After a great deal of soul-searching, I have decided to pursue a new career path that I am deeply passionate about. This has been one of the hardest decisions I have had to make, and it is in no way a reflection of my experience here at ${company}.`,
      Formal: `I have elected to pursue a career transition that is in alignment with my long-term professional objectives. This decision was the result of extensive consideration regarding the trajectory of my career.`,
    },
    Relocation: {
      Standard: `Due to an upcoming relocation, I am unable to continue in my current role. I have greatly appreciated the opportunities I have had at ${company}.`,
      Warm: `As you may know, I will be relocating in the near future, which unfortunately means I will need to leave my position here. I am truly going to miss being part of the ${company} team.`,
      Formal: `Due to a forthcoming relocation, it will no longer be feasible for me to continue in my current capacity at ${company}. I am grateful for the professional opportunities afforded to me during my tenure.`,
    },
    'Personal Reasons': {
      Standard: `I am resigning for personal reasons that require my full attention at this time. I am grateful for the experiences and opportunities I have had during my time here.`,
      Warm: `Due to personal circumstances that require my focus, I have made the difficult decision to step away from my role. I want you to know how much my time at ${company} has meant to me.`,
      Formal: `Personal matters necessitate my departure from ${company} at this time. I wish to convey my sincere appreciation for the professional development and opportunities provided during my employment.`,
    },
    'Going Back to School': {
      Standard: `I have decided to return to school to further my education. This decision will help me grow both personally and professionally in ways that are important to me.`,
      Warm: `I am excited to share that I will be returning to school to pursue further education. While I am looking forward to this new chapter, leaving ${company} is bittersweet -- I have learned so much during my time here.`,
      Formal: `I have elected to resume my academic pursuits in order to enhance my qualifications and professional capabilities. This decision was made with a view toward long-term career advancement.`,
    },
    Other: {
      Standard: `After careful consideration, I have decided that now is the right time for me to move on from my role at ${company}. I am thankful for the experiences I have had here.`,
      Warm: `After giving it a lot of thought, I have come to the conclusion that it is time for me to move on to a new chapter. This has been a genuinely rewarding experience, and I am grateful for every moment at ${company}.`,
      Formal: `After thorough deliberation, I have determined that the appropriate course of action is to conclude my tenure at ${company}. I am appreciative of the opportunities I have been given during my employment.`,
    },
  };

  // --- Gratitude paragraph (based on relationship) ---
  const gratitudeParagraphs: Record<Relationship, Record<Tone, string>> = {
    Great: {
      Standard: `I want to sincerely thank you for your mentorship and support throughout my time here. Working under your leadership has been one of the highlights of my career, and I have grown tremendously because of it.`,
      Warm: `I cannot thank you enough for everything you have done for me. Your mentorship, patience, and genuine support have made such a difference in my career. You are truly one of the best managers I have ever had, and I will always be grateful for the guidance you have given me.`,
      Formal: `I wish to extend my sincere gratitude for your exemplary leadership and mentorship during my tenure. Your guidance has been instrumental in my professional development, and I hold our working relationship in the highest regard.`,
    },
    Good: {
      Standard: `Thank you for your support and guidance during my time at ${company}. I have learned a great deal under your leadership and am grateful for the opportunities to grow.`,
      Warm: `I really appreciate the support and encouragement you have shown me during my time here. You have helped me develop skills I will carry with me throughout my career, and I am thankful for that.`,
      Formal: `I would like to express my appreciation for your leadership and the professional development opportunities you have facilitated. Your support has contributed meaningfully to my growth during my time at ${company}.`,
    },
    Neutral: {
      Standard: `I appreciate the opportunities I have had at ${company} and the experience I have gained during my time here.`,
      Warm: `I am grateful for the experiences and learning opportunities I have had during my time at ${company}. It has been a valuable chapter in my career.`,
      Formal: `I acknowledge and appreciate the professional opportunities and experiences afforded to me during my employment at ${company}.`,
    },
    'Prefer Not to Say': {
      Standard: `I am grateful for the opportunities I have had at ${company} and the professional experiences that have contributed to my growth.`,
      Warm: `I want to express my thanks for the opportunities I have had at ${company}. The experiences and skills I have gained here will stay with me as I move forward.`,
      Formal: `I wish to express my appreciation for the professional opportunities provided during my tenure at ${company}.`,
    },
  };

  // --- Transition paragraph ---
  const transitionParagraphs: Record<Tone, string> = {
    Standard: `I am committed to ensuring a smooth transition. I am happy to help train my replacement, document my current projects, and wrap up any outstanding work during my remaining time.`,
    Warm: `I want to do everything I can to make this transition as seamless as possible. Whether it is training a replacement, documenting processes, or finishing up current projects, I am fully committed to helping in any way I can during my remaining time.`,
    Formal: `I am prepared to assist in facilitating an orderly transition of my responsibilities. I am available to train my successor, prepare comprehensive documentation of ongoing projects, and complete any outstanding obligations during my notice period.`,
  };

  // --- Closing ---
  const closings: Record<Tone, string> = {
    Standard: `I wish ${company} and the team continued success. Thank you again for everything.`,
    Warm: `I truly wish you and the entire ${company} team all the best. I hope our paths cross again in the future, and I will always look back on my time here with fondness.`,
    Formal: `I extend my best wishes to ${company} and its continued success. Thank you for your understanding regarding this matter.`,
  };

  const signoffs: Record<Tone, string> = {
    Standard: 'Sincerely,',
    Warm: 'With gratitude,',
    Formal: 'Respectfully yours,',
  };

  // --- Assemble the letter ---
  const parts: string[] = [];

  parts.push(today);
  parts.push('');
  parts.push(greetings[tone]);
  parts.push('');
  parts.push(openings[tone]);
  parts.push('');
  parts.push(reasonParagraphs[reason][tone]);
  parts.push('');
  parts.push(gratitudeParagraphs[relationship][tone]);

  if (offerTransition) {
    parts.push('');
    parts.push(transitionParagraphs[tone]);
  }

  parts.push('');
  parts.push(closings[tone]);
  parts.push('');
  parts.push(signoffs[tone]);
  parts.push(name);

  return parts.join('\n');
}

export default function ResignationLetterContent() {
  const [name, setName] = useState('');
  const [manager, setManager] = useState('');
  const [company, setCompany] = useState('');
  const [position, setPosition] = useState('');
  const [lastDay, setLastDay] = useState(getDefaultLastDay);
  const [reason, setReason] = useState<Reason>('New Opportunity');
  const [relationship, setRelationship] = useState<Relationship>('Good');
  const [offerTransition, setOfferTransition] = useState(true);
  const [tone, setTone] = useState<Tone>('Standard');
  const [letter, setLetter] = useState('');
  const [generated, setGenerated] = useState(false);

  const reasons: Reason[] = [
    'New Opportunity',
    'Career Change',
    'Relocation',
    'Personal Reasons',
    'Going Back to School',
    'Other',
  ];

  const relationships: Relationship[] = ['Great', 'Good', 'Neutral', 'Prefer Not to Say'];

  const tones: Tone[] = ['Standard', 'Warm', 'Formal'];

  const handleGenerate = () => {
    const result = generateLetter(
      name || '[Your Name]',
      manager || '[Manager Name]',
      company || '[Company Name]',
      position || '[Your Position]',
      lastDay,
      reason,
      relationship,
      offerTransition,
      tone
    );
    setLetter(result);
    setGenerated(true);
  };

  const handleToneChange = (newTone: Tone) => {
    setTone(newTone);
    if (generated) {
      const result = generateLetter(
        name || '[Your Name]',
        manager || '[Manager Name]',
        company || '[Company Name]',
        position || '[Your Position]',
        lastDay,
        reason,
        relationship,
        offerTransition,
        newTone
      );
      setLetter(result);
    }
  };

  const inputClass =
    'w-full border border-gray-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-shadow text-gray-900';
  const labelClass = 'block text-sm font-semibold text-gray-700 mb-1.5';

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-stone-100">
      <ToolHero
        title="Resignation Letter Generator"
        description="Generate a professional resignation letter in seconds. Answer a few questions about your situation and get a polished, respectful letter ready to submit."
        category="Career Tools"
      />

      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Input Form */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Your Details</h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {/* Your Name */}
            <div>
              <label htmlFor="name" className={labelClass}>
                Your Name
              </label>
              <input
                id="name"
                type="text"
                placeholder="Jane Smith"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className={inputClass}
              />
            </div>

            {/* Manager's Name */}
            <div>
              <label htmlFor="manager" className={labelClass}>
                Manager&apos;s Name
              </label>
              <input
                id="manager"
                type="text"
                placeholder="John Davis"
                value={manager}
                onChange={(e) => setManager(e.target.value)}
                className={inputClass}
              />
            </div>

            {/* Company Name */}
            <div>
              <label htmlFor="company" className={labelClass}>
                Company Name
              </label>
              <input
                id="company"
                type="text"
                placeholder="Acme Corp"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                className={inputClass}
              />
            </div>

            {/* Your Position */}
            <div>
              <label htmlFor="position" className={labelClass}>
                Your Position
              </label>
              <input
                id="position"
                type="text"
                placeholder="Software Engineer"
                value={position}
                onChange={(e) => setPosition(e.target.value)}
                className={inputClass}
              />
            </div>

            {/* Last Working Day */}
            <div>
              <label htmlFor="lastDay" className={labelClass}>
                Last Working Day
              </label>
              <input
                id="lastDay"
                type="date"
                value={lastDay}
                onChange={(e) => setLastDay(e.target.value)}
                className={inputClass}
              />
            </div>

            {/* Reason for Leaving */}
            <div>
              <label htmlFor="reason" className={labelClass}>
                Reason for Leaving
              </label>
              <select
                id="reason"
                value={reason}
                onChange={(e) => setReason(e.target.value as Reason)}
                className={inputClass}
              >
                {reasons.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </div>

            {/* Relationship with Manager */}
            <div>
              <label htmlFor="relationship" className={labelClass}>
                Relationship with Manager
              </label>
              <select
                id="relationship"
                value={relationship}
                onChange={(e) => setRelationship(e.target.value as Relationship)}
                className={inputClass}
              >
                {relationships.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </div>

            {/* Offer to help with transition */}
            <div className="flex items-center sm:pt-7">
              <label htmlFor="transition" className="flex items-center gap-3 cursor-pointer">
                <input
                  id="transition"
                  type="checkbox"
                  checked={offerTransition}
                  onChange={(e) => setOfferTransition(e.target.checked)}
                  className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm font-semibold text-gray-700">
                  Offer to help with transition
                </span>
              </label>
            </div>
          </div>

          {/* Generate Button */}
          <div className="mt-8">
            <button
              onClick={handleGenerate}
              className="w-full sm:w-auto bg-gradient-to-r from-blue-600 to-blue-500 text-white font-bold py-3 px-10 rounded-xl shadow-lg hover:from-blue-700 hover:to-blue-600 transition-all text-lg"
            >
              Generate Letter
            </button>
          </div>
        </div>

        {/* Results Section */}
        {generated && (
          <div className="mt-10">
            {/* Tone Tabs */}
            <div className="flex items-center gap-2 mb-6">
              {tones.map((t) => (
                <button
                  key={t}
                  onClick={() => handleToneChange(t)}
                  className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors ${
                    tone === t
                      ? 'bg-blue-600 text-white shadow'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {t}
                </button>
              ))}
              <div className="ml-auto">
                <CopyButton text={letter} label="Copy Letter" />
              </div>
            </div>

            {/* Letter Preview */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 sm:p-12">
              <div className="font-serif text-gray-800 leading-relaxed whitespace-pre-line text-base sm:text-lg">
                {letter}
              </div>
            </div>
          </div>
        )}

        {/* Educational Content */}
        <section className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 mt-8">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">How to Resign Professionally</h3>
          <div className="space-y-4 text-gray-700 leading-relaxed">
            <p>
              Resigning from a job is one of the most significant moments in your career, and how you handle it can have lasting consequences. A professional resignation preserves relationships, protects your reputation, and ensures you leave on good terms. According to the <a href="https://www.shrm.org/topics-tools/tools/how-to-guides/how-to-handle-employee-resignation" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">Society for Human Resource Management (SHRM)</a>, the way you exit a company is often remembered longer than the work you did while there. Former managers and colleagues become part of your professional network for years to come, and a graceful departure keeps those doors open.
            </p>
            <p>
              The standard professional practice is to provide at least two weeks of notice, though senior roles or specialized positions may warrant three to four weeks. Always check your employment contract or company handbook for specific notice period requirements before submitting your letter. Your resignation letter should be concise, positive in tone, and free of grievances. Even if your experience was negative, the resignation letter is not the place to air complaints. Save constructive feedback for the exit interview, if one is offered.
            </p>
            <p>
              Before submitting your written resignation, it is considered best practice to have a private, in-person conversation with your direct manager. This shows respect and gives them a chance to hear the news directly from you rather than through HR channels. After the conversation, follow up immediately with your formal written letter so there is a clear record of your notice date and last working day.
            </p>
            <h4 className="text-lg font-bold text-gray-900 pt-2">Resignation Best Practices</h4>
            <ul className="list-disc pl-5 space-y-2">
              <li><strong>Tell your manager first.</strong> Never let your boss find out you are leaving through the rumor mill or from HR. A direct, respectful conversation should always come first.</li>
              <li><strong>Put it in writing.</strong> A formal resignation letter creates a clear record of your last day and notice period. Keep it to one page and stick to the facts.</li>
              <li><strong>Offer to help with the transition.</strong> Volunteering to train your replacement or document your processes demonstrates professionalism and leaves a strong final impression.</li>
              <li><strong>Stay engaged until your last day.</strong> Resist the temptation to coast during your notice period. Finishing strong reinforces the positive reputation you have built.</li>
              <li><strong>Do not burn bridges.</strong> The professional world is smaller than you think. A manager you resign from today could be a reference, a client, or even a future boss.</li>
            </ul>
            <h4 className="text-lg font-bold text-gray-900 pt-2">Know Your Rights</h4>
            <p>
              In the United States, most employment is &quot;at-will,&quot; meaning you can resign at any time for any reason, and your employer can also end the relationship at any time. However, if you have an employment contract, there may be specific clauses regarding notice periods, non-compete agreements, or other obligations. The <a href="https://www.dol.gov/general/topic/employment" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">U.S. Department of Labor</a> provides resources on employment rights that are worth reviewing before making your move.
            </p>
            <p className="mt-6 pt-4 border-t border-gray-200 text-sm text-gray-500">
              <strong>Sources &amp; Further Reading:</strong>{' '}
              <a href="https://www.indeed.com/career-advice/starting-new-job/how-to-write-a-resignation-letter" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">Indeed: How to Write a Resignation Letter</a>
              {' · '}
              <a href="https://hbr.org/2022/07/how-to-quit-your-job-without-burning-bridges" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">Harvard Business Review: How to Quit Without Burning Bridges</a>
              {' · '}
              <a href="https://www.shrm.org/topics-tools/tools/how-to-guides/how-to-handle-employee-resignation" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">SHRM: Handling Employee Resignations</a>
            </p>
          </div>
        </section>

        {/* CTA */}
        <ToolCTA
          headline="Starting a new role? Make sure your resume is ready."
          description="Use our AI-powered resume builder to create a polished, ATS-friendly resume that highlights your strengths and lands interviews."
          buttonText="Update My Resume Free"
          buttonHref="https://app.superpowerresume.com/sign-up"
        />
      </div>
    </div>
  );
}
